import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

// Workspace tabs — literal tabs ACROSS the OS (Aaron, 2026-08-13): Inbox held
// open in one tab, Finance in another, a stage workspace in a third; jump
// between them like browser tabs, inside the app. Newest tab first.
//
// Two ways in:
//  - MODULE surfaces (Inbox, Finance, Deals…) register automatically on
//    visit; the module's tab remembers the exact sub-page you were last on,
//    so clicking "Inbox" returns you to the view you left.
//  - ENTITY work surfaces (a stage workspace, an expression, a property)
//    register with useRegisterWorkspaceTab once their title is known.
//
// Session-scoped: tabs survive reloads within the session, not sign-ins.

const Ctx = createContext(null)
const STORE_KEY = 'chatrone_workspace_tabs'
const MAX_TABS = 12

// Auto-registered module surfaces. `match` lists the exact pathnames that
// belong to the module (entity detail pages register themselves instead —
// that's why '/projects' matches the list page but '/projects/:id' doesn't).
export const MODULE_TABS = [
  { id: 'module:inbox',         match: ['/inbox', '/inbox/gmail', '/inbox/tracking'], title: 'Inbox' },
  { id: 'module:finance',       match: ['/finance', '/reports'],  title: 'Finance' },
  { id: 'module:tasks',         match: ['/tasks'],                title: 'Tasks' },
  { id: 'module:messages',      match: ['/messages'],             title: 'Messages' },
  { id: 'module:comms',         match: ['/comms'],                title: 'Comms' },
  { id: 'module:calendar',      match: ['/calendar'],             title: 'Calendar' },
  { id: 'module:contacts',      match: ['/contacts'],             title: 'Contacts' },
  { id: 'module:companies',     match: ['/companies'],            title: 'Companies' },
  { id: 'module:clients',       match: ['/clients'],              title: 'Clients' },
  { id: 'module:properties',    match: ['/properties'],           title: 'Properties' },
  { id: 'module:projects',      match: ['/projects'],             title: 'Versions' },
  { id: 'module:deals',         match: ['/deals'],                title: 'Deals' },
  { id: 'module:submissions',   match: ['/submissions'],          title: 'Submissions' },
  { id: 'module:opportunities', match: ['/opportunities'],        title: 'Opportunities' },
  { id: 'module:research',      match: ['/research', '/audience'], title: 'Research' },
  { id: 'module:hub',           match: ['/studio/hub'],           title: 'Slate' },
  { id: 'module:design',        match: ['/studio/design'],        title: 'Design Takes' },
  { id: 'module:janetq',        match: ['/janet/queue'],          title: 'Janet Queue' },
  { id: 'module:janet',         match: ['/janet/chat'],           title: 'Janet' },
]

export function WorkspaceTabsProvider({ children }) {
  const location = useLocation()
  const [tabs, setTabs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '[]') } catch { return [] }
  })

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(tabs)) } catch { /* private mode */ }
  }, [tabs])

  // Newest first: a new tab lands at the LEFT; revisiting an open tab updates
  // it in place (no reshuffle — stable positions are what make tabs usable).
  const openTab = useCallback(({ id, path, title, kind, matches }) => {
    if (!id || !path || !title) return
    setTabs(prev => {
      const i = prev.findIndex(t => t.id === id)
      if (i >= 0) {
        const cur = prev[i]
        if (cur.path === path && cur.title === title) return prev
        const next = [...prev]; next[i] = { ...cur, path, title, kind, matches }
        return next
      }
      return [{ id, path, title, kind, matches }, ...prev].slice(0, MAX_TABS)
    })
  }, [])

  const closeTab = useCallback((id) => {
    setTabs(prev => prev.filter(t => t.id !== id))
  }, [])

  // Slide a tab into a new position: place `dragId` before/after `overId`.
  // No-ops when the move wouldn't change anything, so live drag-over calls
  // don't thrash state.
  const reorderTab = useCallback((dragId, overId, before) => {
    setTabs(prev => {
      const from = prev.findIndex(t => t.id === dragId)
      const over = prev.findIndex(t => t.id === overId)
      if (from < 0 || over < 0 || dragId === overId) return prev
      let insert = before ? over : over + 1
      if (insert === from || insert === from + 1) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      if (from < insert) insert--
      next.splice(insert, 0, moved)
      return next
    })
  }, [])

  // Module surfaces tab themselves on visit, remembering the exact sub-page.
  useEffect(() => {
    const mod = MODULE_TABS.find(m => m.match.includes(location.pathname))
    if (mod) {
      openTab({
        id: mod.id,
        path: location.pathname + (location.search || ''),
        title: mod.title,
        kind: 'module',
        matches: mod.match,
      })
    }
  }, [location.pathname, location.search, openTab])

  return <Ctx.Provider value={{ tabs, openTab, closeTab, reorderTab }}>{children}</Ctx.Provider>
}

// Null-safe: a page outside the provider registers into the void, not a crash.
export function useWorkspaceTabs() {
  return useContext(Ctx) || { tabs: [], openTab: () => {}, closeTab: () => {}, reorderTab: () => {} }
}

// One-liner for entity work-surface pages: register (or retitle) this
// surface's tab once its title is known. kind drives the tab's category mark.
export function useRegisterWorkspaceTab(path, title, kind) {
  const { openTab } = useWorkspaceTabs()
  useEffect(() => {
    if (path && title) openTab({ id: path, path, title, kind })
  }, [path, title, kind, openTab])
}
